import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_skill-bridge-hr/artifacts/gxk71j6k_free-photo-of-a-group-of-people-standing-in-a-circle-removebg-preview.png";

const navLinks = [
  { label: "Services", path: "/services" },
  { label: "For Employers", path: "/employers" },
  { label: "For Job Seekers", path: "/careers" },
  { label: "About Us", path: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="main-navbar"
      className={`sticky top-0 z-50 bg-white border-b border-slate-200 transition-all duration-300 ${scrolled ? "nav-scrolled" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="Skill Bridge HR" className="h-10 md:h-14 w-auto" />
            <div className="hidden sm:block">
              <span className="font-heading font-bold text-[var(--sb-blue)] text-lg md:text-xl leading-tight block">SKILL BRIDGE</span>
              <span className="font-heading text-[var(--sb-blue)] text-[10px] md:text-xs tracking-[0.15em] uppercase block">Human Resources Pvt. Ltd.</span>
            </div>
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-bold tracking-wide uppercase transition-colors ${
                  isActive(link.path) ? "text-[var(--sb-blue)]" : "text-slate-700 hover:text-[var(--sb-blue)]"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="bg-[var(--sb-orange)] text-white px-6 py-2.5 font-bold uppercase tracking-wide text-sm hover:bg-[#C9661A] transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Contact Us
            </Link>
          </div>

          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="p-2 text-slate-700">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-white">
                <SheetTitle className="font-heading text-[var(--sb-blue)] text-xl font-bold">Menu</SheetTitle>
                <div className="flex flex-col gap-1 mt-6">
                  <Link to="/" onClick={() => setOpen(false)} className="py-3 px-4 text-sm font-bold uppercase tracking-wide text-slate-700 hover:bg-slate-50 hover:text-[var(--sb-blue)] transition-colors">
                    Home
                  </Link>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`py-3 px-4 text-sm font-bold uppercase tracking-wide transition-colors ${
                        isActive(link.path) ? "text-[var(--sb-blue)] bg-blue-50" : "text-slate-700 hover:bg-slate-50 hover:text-[var(--sb-blue)]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="mt-4 mx-4 bg-[var(--sb-orange)] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm text-center hover:bg-[#C9661A] transition-colors"
                  >
                    Contact Us
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
